import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  viewChild,
} from '@angular/core';

import { ECharts, init } from 'echarts';

import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import type { AggregatedEvaluationsScore } from '@core/evaluations-types';
import { getRatingThumb } from '@core/evaluations-util';
import { TranslatePipe } from '@core/i18n-legacy';
import { Theme } from '@core/theme-util';
import { IconComponent, IconSizes } from '@core/user-interface-legacy';

import { createChartTooltip } from '../chart-tooltip/create-chart-tooltip';
import type {
  EvaluationRatingScoreFontSize,
  EvaluationRatingScoreFontWeight,
} from '../evaluation-rating-score/evaluation-rating-score.component';
import { EvaluationRatingScoreComponent } from '../evaluation-rating-score/evaluation-rating-score.component';
import { RatingsChartTooltipComponent } from './ratings-chart-tooltip/ratings-chart-tooltip.component';

const BASE_CHART_SIZE = 130;
const CHART_LINE_WIDTH = 10;

@Component({
  selector: 'rt-candidate-ratings-score-chart',
  templateUrl: './candidate-ratings-score-chart.component.html',
  styleUrl: './candidate-ratings-score-chart.component.less',
  host: {
    '[style.width.px]': 'size()',
    '[style.height.px]': 'size()',
  },
  imports: [EvaluationRatingScoreComponent, IconComponent, TranslatePipe],
})
export class CandidateRatingsScoreChartComponent {
  private readonly destroyRef = inject(DestroyRef);

  private readonly chartTooltip = createChartTooltip({
    injector: inject(Injector),
    component: RatingsChartTooltipComponent,
    inputs: () => ({ score: this.score(), evaluationScale: this.evaluationScale() }),
  });

  public evaluationScale = input.required<RatingScale>();
  public score = input.required<AggregatedEvaluationsScore>();
  public isHidden = input(false);
  public showTooltip = input(false);
  public theme = input.required<Theme>();
  public size = input(130);
  public showScoreCaption = input(true);
  public scoreIconSize = input<keyof typeof IconSizes>('medium');
  public scoreFontSize = input<EvaluationRatingScoreFontSize>('medium');
  public scoreFontWeight = input<EvaluationRatingScoreFontWeight>('bold');

  protected container = viewChild.required<ElementRef<HTMLDivElement>>('chartContainer');

  protected isEmpty = computed(() => {
    const ratings = this.score().ratings;

    if (!Array.isArray(ratings)) {
      return true;
    }

    return ratings.reduce((total, { answerCount }) => total + answerCount, 0) === 0;
  });

  protected emptyScoreClass = computed(
    () => `empty-score font-size-${this.scoreFontSize()} font-weight-${this.scoreFontWeight()}`,
  );

  private chartOptions = computed(() => {
    const average = this.score().average;
    const isBlank = this.isEmpty() || this.isHidden();
    const interactive = this.showTooltip() && !this.isEmpty();
    const thumbOption = getRatingThumb(average, this.evaluationScale().presentation);
    const lineWidth = (CHART_LINE_WIDTH / BASE_CHART_SIZE) * this.size();

    return {
      animation: false,
      tooltip: interactive ? { ...this.chartTooltip, trigger: 'item' } : { show: false },
      series: [
        ...(interactive
          ? [
              {
                type: 'pie',
                radius: ['0%', '100%'],
                silent: false,
                emphasis: { disabled: true },
                labelLine: { show: false },
                label: { show: false },
                itemStyle: { color: 'transparent' },
                data: [{ value: 1 }],
              },
            ]
          : []),
        {
          type: 'gauge',
          startAngle: 225,
          endAngle: -45,
          min: 0,
          max: 100,
          radius: '100%',
          silent: true,
          progress: {
            show: true,
            roundCap: true,
            width: lineWidth,
            itemStyle: {
              color: isBlank ? 'transparent' : this.theme()[thumbOption.chartColor],
            },
          },
          axisLine: {
            roundCap: true,
            lineStyle: {
              width: lineWidth,
              color: <Array<[number, string]>>[[1, this.theme().colorBgNeutralStrongDefault]],
            },
          },
          pointer: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          anchor: { show: false },
          title: { show: false },
          detail: { show: false },
          data: [{ value: isBlank ? 0 : average }],
        },
      ],
    };
  });

  private chart?: ECharts;
  private _updateChart = effect(() => {
    const chartOptions = this.chartOptions();
    if (chartOptions && this.chart) {
      this.chart.setOption(chartOptions);
    }
  });

  protected ngAfterViewInit() {
    const element = this.container().nativeElement;

    const resizeObserver = new ResizeObserver(() => {
      if (!element.clientWidth || !element.clientHeight) {
        return;
      }

      if (this.chart) {
        this.chart.resize();
      } else {
        this.chart = init(element, undefined, { renderer: 'svg' });
        this.chart.setOption(this.chartOptions());
      }
    });
    resizeObserver.observe(element);

    this.destroyRef.onDestroy(() => {
      resizeObserver.disconnect();
      this.chart?.dispose();
    });
  }
}
